import { createTheme, type ThemeOptions } from '@mui/material/styles'
import type { CSSProperties } from 'react'

// Phase 110 M2 V2 typography ladder: add `body`, `subtitle`, `monospace` as new
// MUI variants. `caption` is also explicitly declared (matches MUI default but
// makes the V2 contract visible in the theme). Existing variants (h1-h6, body1,
// body2, subtitle1, subtitle2, button) are preserved bit-equivalently — Banrural
// callsites that reference them keep rendering identically (R7 safety).
declare module '@mui/material/styles' {
  interface TypographyVariants {
    body: CSSProperties
    subtitle: CSSProperties
    monospace: CSSProperties
  }
  interface TypographyVariantsOptions {
    body?: CSSProperties
    subtitle?: CSSProperties
    monospace?: CSSProperties
  }

  interface Palette {
    tertiary: {
      main: string
      light: string
      dark: string
      contrastText: string
    }
  }

  interface PaletteOptions {
    tertiary?: {
      main: string
      light?: string
      dark?: string
      contrastText?: string
    }
  }
}

// Brand colors — shared reference
const COLORS = {
  fireBrick: '#A22432',
  fireBrickLight: '#C94F5A',
  darkRed: '#8B0000',
  graniteGray: '#333333',
  graniteGrayDark: '#1F1F1F',
  frenchGray: '#BBBDBE',
  battleshipGray: '#808184',
  backgroundLight: '#f8f6f6',
}

// Light palette (unchanged from original theme)
const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: COLORS.fireBrick,
    light: COLORS.fireBrickLight,
    dark: COLORS.darkRed,
    contrastText: '#ffffff',
  },
  secondary: {
    main: COLORS.graniteGray,
    light: COLORS.frenchGray,
    dark: COLORS.graniteGrayDark,
    contrastText: '#ffffff',
  },
  tertiary: {
    main: COLORS.battleshipGray,
    light: '#9B9D9F',
    dark: '#606163',
    contrastText: '#ffffff',
  },
  background: {
    default: COLORS.backgroundLight,
    paper: '#ffffff',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
}

// Dark palette — per design.md §Dark Mode Palette (FR-B7)
// Fire Brick brightened to #D4565F for WCAG AA contrast on dark backgrounds
const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#D4565F',
    light: '#E88088',
    dark: COLORS.fireBrick, // Original as accent
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#E0E0E0',
    light: '#F5F5F5',
    dark: '#BDBDBD',
    contrastText: '#121212',
  },
  tertiary: {
    main: '#9B9D9F',
    light: '#BBBDBE',
    dark: '#808184',
    contrastText: '#121212',
  },
  background: {
    default: '#121212',
    paper: '#1E1E1E',
  },
  text: {
    primary: '#E0E0E0',
    secondary: '#B0B0B0',
  },
}

// Phase 110 M2: register the new variants with MUI Typography's `variant` prop type
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body: true
    subtitle: true
    monospace: true
  }
}

// Shared config (typography, shape, breakpoints, component overrides)
const sharedConfig: Omit<ThemeOptions, 'palette'> = {
  typography: {
    fontFamily: '"Red Hat Display", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.43 },
    button: { textTransform: 'none', fontWeight: 500 },
    // Phase 110 M2 V2 ladder additions (new semantic variants, no conflict with MUI defaults)
    body: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43 },
    subtitle: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66 },
    monospace: { fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace', fontWeight: 500, lineHeight: 1.5 },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        // Phase 110 M2 V2: map new variants to sensible HTML elements
        variantMapping: {
          body: 'p',
          subtitle: 'p',
          monospace: 'span',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 14,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: t.palette.divider,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 11,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
}

/** Create a complete MUI theme for the given mode */
export function createAppTheme(mode: 'light' | 'dark') {
  const palette = mode === 'dark' ? darkPalette : lightPalette
  return createTheme({ palette, ...sharedConfig })
}

// Default export for backward compatibility (light theme)
const theme = createAppTheme('light')
export default theme
